import { createContext } from 'preact';
import { useState, useEffect, useCallback, useMemo } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import type { Route, RouterConfig, NavigationContext } from './types';

/**
 * Router context value
 */
export interface RouterContextValue {
  /** Current route path */
  currentPath: string;
  /** Navigate to a new path (async to support animations) */
  navigate: (path: string) => Promise<void>;
  /** Whether a navigation is currently in progress */
  isNavigating: boolean;
}

/**
 * Router context for accessing router state
 */
export const RouterContext = createContext<RouterContextValue | null>(null);

/**
 * Router component props
 */
export interface RouterProps {
  /** Router configuration */
  config: RouterConfig;
  /** Children to render */
  children?: ComponentChildren;
}

/**
 * Find matching route for a given path
 */
function findRoute(path: string, routes: Route[]): Route | null {
  // Normalize path (remove trailing slash, handle empty string)
  let normalizedPath = path === '' ? '/' : path;
  if (normalizedPath.length > 1 && normalizedPath.endsWith('/')) {
    normalizedPath = normalizedPath.slice(0, -1);
  }

  // Find exact match
  const route = routes.find((r) => r.path === normalizedPath);
  return route ?? null;
}

/**
 * Router component that manages client-side navigation with animation support
 */
export function Router({ config, children }: RouterProps) {
  const [currentPath, setCurrentPath] = useState(() =>
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );
  const [isNavigating, setIsNavigating] = useState(false);

  /**
   * Perform navigation with animation hooks
   */
  const navigate = useCallback(
    async (newPath: string, isPopState = false) => {
      // Don't navigate if already at this path
      if (newPath === currentPath) {
        return;
      }

      setIsNavigating(true);

      const context: NavigationContext = {
        from: currentPath,
        to: newPath,
        isPopState,
      };

      try {
        // Run beforeNavigate hook (exit animations)
        if (config.beforeNavigate) {
          await config.beforeNavigate(context);
        }

        // Update the current path
        setCurrentPath(newPath);

        // Update browser history (unless this is a popstate event)
        if (!isPopState && typeof window !== 'undefined') {
          window.history.pushState({ path: newPath }, '', newPath);
        }

        // Update document title if route has one
        const route = findRoute(newPath, config.routes);
        if (route?.title && typeof document !== 'undefined') {
          document.title = route.title;
        }

        // Run afterNavigate hook (enter animations)
        if (config.afterNavigate) {
          await config.afterNavigate(context);
        }
      } finally {
        setIsNavigating(false);
      }
    },
    [currentPath, config]
  );

  /**
   * Handle browser back/forward buttons
   */
  useEffect(() => {
    // Skip during SSR
    if (typeof window === 'undefined') return;

    const handlePopState = (event: PopStateEvent) => {
      const newPath = window.location.pathname;
      // Use the state from history if available, otherwise use current pathname
      const state = event.state as { path?: string } | null;
      const path = state?.path ?? newPath;
      void navigate(path, true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  /**
   * Set initial history state
   */
  useEffect(() => {
    // Skip during SSR
    if (typeof window === 'undefined') return;

    // Replace current history state with initial path (preserve hash)
    window.history.replaceState({ path: currentPath }, '', currentPath + window.location.hash);
  }, []);

  /**
   * Router context value
   */
  const contextValue = useMemo<RouterContextValue>(
    () => ({
      currentPath,
      navigate,
      isNavigating,
    }),
    [currentPath, navigate, isNavigating]
  );

  /**
   * Find current route component
   */
  const currentRoute = findRoute(currentPath, config.routes);
  const fallbackRoute = config.fallback
    ? findRoute(config.fallback, config.routes)
    : config.routes[0];
  const route = currentRoute ?? fallbackRoute;

  if (!route) {
    throw new Error('No route found and no fallback configured');
  }

  const RouteComponent = route.component;

  return (
    <RouterContext.Provider value={contextValue}>
      <RouteComponent />
      {children}
    </RouterContext.Provider>
  );
}
